// Comprehensive Critical Analysis Dossier: Ratnadwip & Ankita
// Sourced from chat_context/context.txt and longitudinal communications
// Modularly structured to allow continuous updates as relationship develops

export interface AnalysisSection {
  id: string;
  number: string;
  title: string;
  category: 'overview' | 'timeline' | 'psychology' | 'conflict' | 'action_plan' | 'updates';
  summary: string;
  content: string[];
  keyTakeaways?: string[];
  bulletPoints?: Array<{ label: string; detail: string }>;
}

export interface ProgressUpdate {
  date: string;
  title: string;
  author: string;
  summary: string;
  observations: string[];
  strategicAdjustments: string[];
}

export interface CriticalAnalysisReport {
  metadata: {
    title: string;
    subject: string;
    status: string;
    statusTone: 'warning' | 'critical' | 'stable' | 'evolving';
    lastUpdated: string;
    nextMilestone: string;
    author: string;
    version: string;
  };
  metrics: {
    totalRecordedMessages: number;
    timelineMonths: number;
    conflictStage: string;
    inPersonEncounterEta: string;
    attachmentDynamic: string;
    reconciliationProbability: string;
  };
  executiveSummary: string;
  sections: AnalysisSection[];
  progressLog: ProgressUpdate[];
}

export const RELATIONSHIP_ANALYSIS: CriticalAnalysisReport = {
  metadata: {
    title: 'CRITICAL RELATIONSHIP ANALYSIS & STRATEGIC DOSSIER',
    subject: 'Ratnadwip Sarkar & Ankita Aich',
    status: 'COMMUNICATION STRAINED • SPACE REQUESTED',
    statusTone: 'warning',
    lastUpdated: '24 October 2026',
    nextMilestone: 'Upcoming In-Person Meeting (In ~80 Hours)',
    author: 'Behavioral & Relational Analytics',
    version: '1.4.0',
  },
  metrics: {
    totalRecordedMessages: 2515,
    timelineMonths: 4.5,
    conflictStage: 'Acute Boundary Declaration (Post-Kolkata)',
    inPersonEncounterEta: '~80 Hours Remaining',
    attachmentDynamic: 'Anxious-Preoccupied (Ratnadwip) ⇄ Fearful-Avoidant (Ankita)',
    reconciliationProbability: 'Dependent on Non-Defensive In-Person Dialogue',
  },
  executiveSummary:
    'This report documents and clinically assesses the relational dynamic between Ratnadwip and Ankita through 24 October 2026. The connection has evolved from collegiate friendship into deep emotional, romantic, and physical closeness (marked by hand-holding, lap-sitting, kissing, and bike rides during Durga Puja). However, unresolved questions of commitment, recurrent self-deprecating warnings from Ankita ("I\'ll ruin your life", "Stay away"), and an unresolved friction during a recent Kolkata visit have precipitated an acute withdrawal. Ratnadwip is currently experiencing severe anxiety, sleep deprivation, and appetite loss. A confirmed upcoming in-person encounter represents the pivotal inflection point.',
  sections: [
    {
      id: 'timeline-milestones',
      number: '01',
      title: 'Longitudinal Relationship Timeline',
      category: 'timeline',
      summary: 'Chronological progression of intimacy from initial friendship to the October breakdown.',
      content: [
        'Late August 2026 (Rakhi & Shared Birthday, 28-29 Aug): The turning point where mutual communication shifted into deeply personal territory. Sharing a 29 August birthday created an early sense of special alignment.',
        '8 September 2026 (Teacher’s Day): Attended college festivities in matching coordinated outfits. Hand-holding and biting on the return train. First documented emergence of Ankita’s self-protective disclaimers regarding relationships.',
        'Late September 2026 (Hackathon Phase): Substantial increase in physical closeness: lap-sitting, sustained hand-holding, train kissing, and hotel intimacy during city visits.',
        '7 October 2026 (Physical Pacing Discussion): Ankita proposed getting a private room together. Ratnadwip exercised caution, seeking to balance intense physical attraction against relational uncertainty without triggering rejection sensitivity in Ankita.',
        'Durga Puja 2026 (Peak Shared Warmth): Happy outing together, social drinking, kissing, and bike ride. Warm, affectionate romantic calls continued unbroken directly afterward.',
        '19-21 October 2026 (The Kolkata Incident): Ratnadwip travelled to Kolkata to see Ankita. Logistics or interpersonal friction triggered severe irritation and anger in Ankita.',
        '22-23 October 2026 (Communication Breakdown): Rapid withdrawal culminating in Ankita’s boundary declaration: "Ami tor theke durey thakte chai... I’m not interested with u... So ami disturb koris na."',
        'Upcoming In-Person Encounter: Confirmed plan to meet in ~80 hours, providing an authentic physical venue for resolution.',
      ],
      keyTakeaways: [
        'The relationship has an indisputable foundation of genuine physical and romantic attraction.',
        'Affectionate peaks have frequently triggered self-protective defensive pull-backs.',
        'The current silence is acute and reactive, not an unprovoked gradual fade.',
      ],
    },
    {
      id: 'ankita-communication',
      number: '02',
      title: 'Decoding Ankita’s Ambivalence & Verbal Cues',
      category: 'psychology',
      summary: 'Examining the deep contradiction between Ankita’s self-deprecating warnings and physical intimacy.',
      content: [
        'Throughout the trajectory, Ankita has voiced extreme preemptive self-blame:',
        '• "Stay away from me."',
        '• "I am bad / I will ruin your life."',
        '• "I will give you trauma."',
        '• "There is no future for us."',
        'In clinical relational psychology, such statements typically indicate Fearful-Avoidant attachment (Disorganized Attachment). The individual experiences genuine attraction and longing for intimacy, but feels profound vulnerability and unworthiness.',
        'By declaring herself "bad" or claiming "there is no future", she attempts to preemptively manage expectation and shield herself from perceived eventual abandonment.',
        'Crucially, two opposing truths coexist: (1) She feels authentic affection, attraction, and joy when close to Ratnadwip; (2) She experiences acute fear of commitment, emotional exposure, or loss of control.',
        'Her latest messages ("Ami tor theke durey thakte chai") reflect an activated threat response following the Kolkata incident rather than a settled, cold conclusion.',
      ],
      bulletPoints: [
        { label: 'Push-Pull Dynamic', detail: 'Intimacy triggers vulnerability -> Vulnerability triggers flight or boundary enforcement.' },
        { label: 'Verbal Warning as Shield', detail: '"I told you I was bad" functions as insurance against future relational failure.' },
        { label: 'Reactive vs. Reflective', detail: 'Her message was sent in anger and physical fatigue ("amr sorir bhlo nei"), not calm contemplation.' },
      ],
    },
    {
      id: 'ratnadwip-psychology',
      number: '03',
      title: 'Ratnadwip’s Emotional Position & Anxious Loop',
      category: 'psychology',
      summary: 'Analysis of acute anxiety, cognitive rumination, and somatic distress during holiday silence.',
      content: [
        'Ratnadwip is displaying classic signs of Anxious-Preoccupied hyperactivation in response to romantic distance.',
        'The absence of routine college classes and project deadlines has created an "unstructured cognitive void." Without external demands, mental bandwidth is entirely consumed by obsessive replay of recent conversations.',
        'The Rumination Cycle:',
        '1. Trigger: Perception of silence or anger from Ankita.',
        '2. Catastrophizing: Belief that the entire relationship is collapsing or was an illusion.',
        '3. Reassurance Urge: Urgent impulse to call, text, or apologize profusely.',
        '4. Restraint Conflict: Knowing messaging may worsen things, resulting in paralyzed waiting.',
        '5. Somatic Impact: Appetite suppression, insomnia, persistent chest tightness.',
        'This physiological state compromises judgment and magnifies perceived time (80 hours feels unbearable). Immediate somatic regulation is required before attempting in-person dialogue.',
      ],
      keyTakeaways: [
        'Ratnadwip must separate her silence from his intrinsic self-worth.',
        'Giving space is not passive defeat; it is an active, disciplined gift of emotional oxygen.',
        'Physical health (nourishment, hydration, sleep) must be restored immediately.',
      ],
    },
    {
      id: 'conflict-hypotheses',
      number: '04',
      title: 'Root Cause Diagnostics: The 4 Conflict Hypotheses',
      category: 'conflict',
      summary: 'Systematic examination of plausible drivers behind the current rupture.',
      content: [
        'Hypothesis A: Acute Kolkata Resentment. Logistical tension, unexpected arrival, or perceived violation of personal schedule during the Kolkata visit left her feeling pressured or overwhelmed.',
        'Hypothesis B: Fear of Escalation & Relational Debt. Following the intense closeness of Durga Puja and the room discussion, she may have felt things were accelerating faster than her emotional defenses could accommodate.',
        'Hypothesis C: Defensive Preemption. Fearing she cannot sustain the relationship or that she will hurt him, she is using the Kolkata friction as a convenient escape hatch to exit before becoming more vulnerable.',
        'Hypothesis D: Temporary Sensory Overload. She was physically unwell ("amr sorir bhlo nei"), burdened by college/family stress, and reacted harshly to persistent contact.',
        'Assessment: The reality is likely a combination of A and D triggering B. In-person conversation is the only diagnostic tool capable of uncovering the primary driver.',
      ],
      bulletPoints: [
        { label: 'Hypothesis A Weight: 35%', detail: 'Immediate catalyst was directly tied to the Kolkata visit.' },
        { label: 'Hypothesis B Weight: 30%', detail: 'Underlying ambivalence regarding relational trajectory.' },
        { label: 'Hypothesis C Weight: 15%', detail: 'Fear-driven avoidance mechanism.' },
        { label: 'Hypothesis D Weight: 20%', detail: 'Acute fatigue and irritation magnifying minor issues.' },
      ],
    },
    {
      id: 'meeting-roadmap',
      number: '05',
      title: 'Strategic Roadmap for Upcoming In-Person Encounter',
      category: 'action_plan',
      summary: 'Tactical, psychological protocol for the confirmed meeting in ~80 hours.',
      content: [
        'A face-to-face meeting is confirmed. This gives Ratnadwip an authentic advantage, provided he does not arrive emotionally desperate or demanding immediate security.',
        'Phase 1: Grounded Calm & Non-Defensiveness (Minutes 0 - 15)',
        '• Do not open with frantic apologies, guilt trips, or demands for an explanation.',
        '• Greet her warmly and neutrally. Let her body language set the initial distance.',
        '• Invite her perspective: "I know things felt tense after Kolkata and Friday. I want to understand how it felt from your side without interrupting."',
        'Phase 2: Radical Active Listening (Minutes 15 - 35)',
        '• Allow her to express irritation or frustration without debating her facts.',
        '• Validate emotional impact before explaining intent: "I see why that frustrated you" vs. "That wasn’t what I meant."',
        'Phase 3: Honest, Centered Self-Expression (Minutes 35 - 50)',
        '• Own your genuine feelings calmly: "I care about you deeply, and the connection we shared at Puja was very real to me."',
        '• State boundaries with dignity: "I want to be in your life, but I also respect your freedom. If you genuinely want space or friendship, I will honor that."',
        'Phase 4: Closure or Reset Agreement (Minutes 50 - 60+)',
        '• Do not force a binding contract. Agree on the immediate next micro-step: whether to take a couple of calm days or resume light, unpressured contact.',
      ],
      keyTakeaways: [
        'Never beg for affection; dignity is magnetic and safe.',
        'Listening disarms hostility faster than argument ever could.',
        'Accept whatever truth she offers with emotional maturity.',
      ],
    },
    {
      id: 'immediate-grounding',
      number: '06',
      title: 'Emergency Self-Regulation Protocol',
      category: 'action_plan',
      summary: 'Mandatory behavioral guidelines for Ratnadwip during the countdown window.',
      content: [
        '1. Digital Fasting on Chat Logs: Cease rereading past messages and re-checking "last seen" or social profiles. Each check spikes cortisol without producing new data.',
        '2. Somatic Nourishment: Eat small, protein-rich meals even when nausea is present. Dehydration drastically worsens cognitive anxiety loops.',
        '3. Structured Time Blocks: Force physical movement—walk outside, study technical DSA problems, or exercise to burn off excess adrenaline.',
        '4. External Venting: Confide in a close, trusted friend or write down unsent thoughts in a private journal instead of transmitting them to Ankita.',
        '5. Mental Reframing: Whatever happens in the meeting, Ratnadwip will survive, learn, and maintain his worth as a caring, capable person.',
      ],
    },
    {
      id: 'critical-unknowns',
      number: '07',
      title: 'The 9 Critical Unknowns & Unanswered Questions',
      category: 'overview',
      summary: 'Key facts that cannot be assumed and must only be discovered through direct dialogue.',
      content: [
        '1. What exact event or expectation mismatch occurred during the Kolkata trip?',
        '2. Did Ankita experience Ratnadwip’s arrival as romantic or as an uncomfortable pressure?',
        '3. What external family or academic stressors coincided with the October 22-23 period?',
        '4. Was her request for distance permanent, or an instinctive reaction to feeling overwhelmed?',
        '5. How does Ankita interpret the physical intimacy shared during Puja in retrospect?',
        '6. Does she have deep fears about parental discovery or college gossip?',
        '7. Has she experienced past relational betrayal that fuels her "I’ll give you trauma" belief?',
        '8. What does she realistically desire from Ratnadwip in the next 3 months?',
        '9. Can she communicate honestly in-person once the online tension is neutralized?',
      ],
    },
  ],
  progressLog: [
    {
      date: '24 October 2026',
      title: 'Baseline Assessment: Acute Boundary Breakdown',
      author: 'Ratnadwip Sarkar',
      summary: 'Baseline established following the October 23 boundary text. Space instituted ahead of confirmed in-person meeting.',
      observations: [
        'Complete digital radio silence observed to prevent escalation.',
        'Confirmed upcoming meeting in ~80 hours serves as primary resolution checkpoint.',
        'High somatic anxiety noted; appetite and sleep disruption monitored.',
      ],
      strategicAdjustments: [
        'Maintain zero unsolicited messaging until the confirmed meeting.',
        'Prepare grounded, non-defensive listening mindset.',
        'Keep relationship heart archive functional and safe.',
      ],
    },
  ],
};
